import React, { useState } from 'react';
import { PlusCircle, Lightbulb, TrendingUp, TrendingDown } from 'lucide-react';
import { parseFormattedNumber, formatNumberInput, getCurrentWeek } from '../../utils';

export const AddEntryTab = ({ projectSettings = {}, setWeeklyData, weeklyData = {}, showToast, isDark, colors }) => {
  const [formData, setFormData] = useState({
    project: '',
    volume: '',
    pnl: '',
    metricValue: '',
    week: getCurrentWeek()
  });

  const allProjects = Object.keys(projectSettings);

  // Get the metric type for the selected project
  const selectedProject = projectSettings[formData.project];
  const metricType = selectedProject?.metricType || 'points';
  const customMetricName = selectedProject?.customMetricName || 'Custom';

  const getMetricLabel = () => {
    switch (metricType) {
      case 'points': return 'Points Earned';
      case 'volume': return 'Volume ($)';
      case 'fees': return 'Fees Earned ($)';
      case 'custom': return customMetricName;
      default: return 'Points Earned';
    }
  };

  const getMetricPlaceholder = () => {
    switch (metricType) {
      case 'points': return '1.000';
      case 'volume': return '50.000';
      case 'fees': return '100';
      case 'custom': return '1.000';
      default: return '1.000';
    }
  };

  const handleNumberChange = (field, value) => {
    const cleanValue = value.replace(/[^0-9.]/g, '');
    setFormData({ ...formData, [field]: cleanValue });
  };

  // Special handler for PNL that allows negative numbers
  const handlePnlChange = (value) => {
    // Allow minus sign at the start, then only numbers and decimal point
    const cleanValue = value.replace(/[^0-9.\-]/g, '').replace(/(?!^)-/g, '');
    setFormData({ ...formData, pnl: cleanValue });
  };

  const handleNumberBlur = (field) => {
    const value = formData[field];
    if (value) {
      const parsed = parseFormattedNumber(value);
      const formatted = formatNumberInput(parsed);
      setFormData({ ...formData, [field]: formatted });
    }
  };

  const handlePnlBlur = () => {
    const value = formData.pnl;
    if (value) {
      const isNegative = value.startsWith('-');
      const parsed = parseFormattedNumber(value.replace('-', ''));
      const formatted = formatNumberInput(parsed);
      setFormData({ ...formData, pnl: isNegative ? `-${formatted}` : formatted });
    }
  };

  // Get PNL display color and icon
  const getPnlStyle = () => {
    const value = formData.pnl;
    if (!value) return { color: '', icon: null };
    
    const numValue = parseFormattedNumber(value);
    if (numValue < 0) {
      return { 
        color: 'text-red-500 border-red-500/50', 
        icon: <TrendingDown size={16} className="text-red-500" />,
        bgColor: isDark ? 'bg-red-900/20' : 'bg-red-50'
      };
    } else if (numValue > 0) {
      return { 
        color: 'text-green-500 border-green-500/50', 
        icon: <TrendingUp size={16} className="text-green-500" />,
        bgColor: isDark ? 'bg-green-900/20' : 'bg-green-50'
      };
    }
    return { color: '', icon: null };
  };

  const pnlStyle = getPnlStyle();

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.project) {
      alert('Please select a project');
      return;
    }

    const metricVal = parseFormattedNumber(formData.metricValue);
    const pnlValue = parseFormattedNumber(formData.pnl);

    // Build entry with the correct metric field
    // For backwards compatibility, store negative PNL as positive cost
    const entry = {
      id: Date.now(),
      project: formData.project,
      volume: metricType === 'volume' ? metricVal : parseFormattedNumber(formData.volume),
      cost: pnlValue < 0 ? Math.abs(pnlValue) : 0,  // Store costs as positive for backwards compat
      profit: pnlValue > 0 ? pnlValue : 0,          // New field for profits
      pnl: pnlValue,                                 // Store the actual PNL value
      points: metricType === 'points' ? metricVal : 0,
      fees: metricType === 'fees' ? metricVal : 0,
      customMetric: metricType === 'custom' ? metricVal : 0,
      metricType: metricType,
      customMetricName: metricType === 'custom' ? customMetricName : null,
      date: new Date().toISOString()
    };

    const currentWeek = formData.week || getCurrentWeek();
    const updatedWeeks = { ...weeklyData.weeks };
    
    if (!updatedWeeks[currentWeek]) {
      updatedWeeks[currentWeek] = [];
    }
    updatedWeeks[currentWeek].push(entry);

    setWeeklyData({ ...weeklyData, weeks: updatedWeeks });
    setFormData({ project: '', volume: '', pnl: '', metricValue: '', week: currentWeek });
    
    if (showToast) {
      showToast('Activity added successfully!');
    }
  };

  const inputClasses = `w-full border rounded-lg px-4 py-3 focus:border-blue-500 focus:outline-none ${isDark ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`;

  return (
    <div className="space-y-6">
      <div className={`${colors.bgCard} border ${colors.border} rounded-xl p-6 ${colors.cardShadow}`}>
        <h3 className={`text-2xl font-bold mb-6 flex items-center gap-2 ${colors.textPrimary}`}>
          <PlusCircle size={28} className="text-blue-400" />
          Register Activity
        </h3>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className={`block text-sm font-medium mb-2 ${colors.textSecondary}`}>Project *</label>
              <select
                value={formData.project}
                onChange={(e) => setFormData({ ...formData, project: e.target.value, metricValue: '', volume: '' })}
                className={inputClasses}
              >
                <option value="">Select project...</option>
                {allProjects.map(p => {
                  const settings = projectSettings[p];
                  const metric = settings?.metricType || 'points';
                  const metricName = metric === 'custom' ? settings?.customMetricName : metric;
                  return (
                    <option key={p} value={p}>{p} ({metricName})</option>
                  );
                })}
              </select>
              {formData.project && (
                <p className={`text-xs mt-1 ${colors.textSecondary}`}>
                  Tracking: <span className={isDark ? 'text-purple-400' : 'text-purple-600'}>{getMetricLabel()}</span>
                </p>
              )}
            </div>
            <div>
              <label className={`block text-sm font-medium mb-2 ${colors.textSecondary}`}>Week</label>
              <input
                type="text"
                value={formData.week}
                onChange={(e) => setFormData({ ...formData, week: e.target.value })}
                placeholder="2026-02-W4"
                className={inputClasses}
              />
            </div>
          </div>

          {formData.project ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Volume - show only if metric is NOT volume */}
              {metricType !== 'volume' && (
                <div>
                  <label className={`block text-sm font-medium mb-2 ${colors.textSecondary}`}>Volume ($) <span className="text-xs">(optional)</span></label>
                  <input
                    type="text"
                    value={formData.volume}
                    onChange={(e) => handleNumberChange('volume', e.target.value)}
                    onBlur={() => handleNumberBlur('volume')}
                    placeholder="1.000.000"
                    className={inputClasses}
                  />
                </div>
              )}
              
              {/* PNL - allows negative (costs) and positive (profits) */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${colors.textSecondary}`}>
                  PNL ($) <span className="text-xs">(costs − / profits +)</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={formData.pnl}
                    onChange={(e) => handlePnlChange(e.target.value)}
                    onBlur={handlePnlBlur}
                    placeholder="-50 or +120"
                    className={`${inputClasses} ${pnlStyle.color} ${pnlStyle.bgColor || ''} pr-10`}
                  />
                  {pnlStyle.icon && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {pnlStyle.icon}
                    </div>
                  )}
                </div>
                <p className={`text-xs mt-1 ${colors.textSecondary}`}>
                  Use <span className="text-red-500 font-medium">-50</span> for costs, <span className="text-green-500 font-medium">+120</span> for profits
                </p>
              </div>

              {/* Main metric field - dynamic based on project */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>
                  {getMetricLabel()} *
                </label>
                <input
                  type="text"
                  value={formData.metricValue}
                  onChange={(e) => handleNumberChange('metricValue', e.target.value)}
                  onBlur={() => handleNumberBlur('metricValue')}
                  placeholder={getMetricPlaceholder()}
                  className={`${inputClasses} ${isDark ? 'border-purple-500/50' : 'border-purple-300'}`}
                />
              </div>
            </div>
          ) : (
            <div className={`text-center py-8 rounded-lg border-2 border-dashed ${isDark ? 'border-slate-600 text-slate-400' : 'border-gray-300 text-gray-500'}`}>
              <p>👆 Select a project first to see the activity fields</p>
            </div>
          )}

          <button
            type="submit"
            disabled={!formData.project}
            className={`w-full font-semibold py-3 rounded-lg transition-colors flex items-center justify-center gap-2 ${
              formData.project 
                ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                : 'bg-gray-400 text-gray-200 cursor-not-allowed'
            }`}
          >
            <PlusCircle size={20} />
            Add Activity
          </button>
        </form>

        <div className={`mt-6 p-4 rounded-lg border ${isDark ? 'bg-blue-900/20 border-blue-700/30' : 'bg-blue-50 border-blue-200'}`}>
          <h4 className={`font-semibold mb-2 flex items-center gap-2 ${colors.textPrimary}`}>
            <Lightbulb size={18} className={isDark ? 'text-yellow-400' : 'text-yellow-600'} />
            Quick tips
          </h4>
          <ul className={`text-sm space-y-1 ${colors.textSecondary}`}>
            <li>• <strong>Volume:</strong> Total traded/transacted amount (optional for non-volume metrics)</li>
            <li>• <strong>PNL:</strong> Use <span className="text-red-500">negative numbers</span> for costs/losses, <span className="text-green-500">positive</span> for profits</li>
            <li>• <strong>Main metric:</strong> The primary value you're tracking for this airdrop (points, fees, etc.)</li>
            <li>• 💡 Each airdrop tracks its own metric type - set this in the "Airdrops" tab</li>
          </ul>
        </div>
      </div>
    </div>
  );
};